import { EnvironmentInjector, Injectable, inject } from '@angular/core';
import type { CustomSpawnBracketRecord } from '@animus/type/domain';
import type { AngularSignalReactivityLambda } from 'rxdb/plugins/reactivity-angular';
import type {
    AnimusCollectionCreators,
    AnimusCollections,
    AnimusDatabase,
    AnimusSignalCollections,
} from './collections';
import {
    HORDE_RXDB_DATABASE_NAME,
    hordeIntentConflictHandler,
    newestUpdatedAtWinsConflictHandler,
} from './rxdb-factory';
import {
    INDEXED_INTENTS_SCHEMA_VERSION,
    INDEXED_JOURNAL_SCHEMA_VERSION,
    customSpawnBracketsSchema,
    customSpawnSimulatorSessionsSchema,
    hordeAuthoritiesSchema,
    hordeCommitsSchema,
    hordeGameEventsSchema,
    hordeGameIntentsSchema,
    hordePlayerSecretViewsSchema,
    hordeSessionsSchema,
} from './schemas';

/**
 * The application's RxDB database, created eagerly by an app initializer and exposed synchronously.
 *
 * Database creation is asynchronous (IndexedDB open + collection registration), so it cannot happen in a field
 * initializer. {@link provideAnimusRxDB} registers an app initializer that calls {@link initialize} and awaits
 * it; Angular gates bootstrap on app initializers, so by the time any component or store reads {@link database},
 * the instance is set.
 */
@Injectable({ providedIn: 'root' })
export class RxDatabaseService {
    // oxlint-disable-next-line typescript/no-non-null-assertion -- set by initialize() in the eager app initializer before any consumer reads it
    private db!: AnimusDatabase;

    private readonly injector = inject(EnvironmentInjector);

    /** The ready database. Only safe to read after {@link initialize} has resolved. */
    get database(): AnimusDatabase {
        return this.db;
    }

    /**
     * Creates (or reuses) the RxDB instance and registers all collections. Resolving marks the app initializer
     * complete, so `database` is guaranteed set afterwards.
     */
    async initialize(): Promise<void> {
        if (this.db) {
            return;
        }

        this.db = await createAnimusDatabase(this.injector);
    }
}

async function createAnimusDatabase(injector: EnvironmentInjector): Promise<AnimusDatabase> {
    const { createRxDatabase } = await import('rxdb/plugins/core');
    const { addRxPlugin } = await import('rxdb');
    const { RxDBMigrationSchemaPlugin } = await import('rxdb/plugins/migration-schema');
    const { createAnimusReactivityFactory } = await import('@animus/utility/web/rxdb-reactivity');
    const { getRxStorageDexie } = await import('rxdb/plugins/storage-dexie');

    // RxDB requires this plugin for versioned schemas even though this fresh database deliberately supplies
    // no migration strategies and never reads a prior Horde database.
    addRxPlugin(RxDBMigrationSchemaPlugin);
    // multiInstance stays at its default (true): IndexedDB is shared across tabs, so cross-tab
    // change propagation + leader election are required for correctness when two tabs are open.
    // `replicateWebRTC`'s internal `waitForLeadership()` therefore only resolves in the leader tab -
    // callers must treat replication start as best-effort fire-and-forget, never await it in UI flows.
    //
    // Storage moved from `getRxStorageLocalstorage()` to `getRxStorageDexie()` (the free IndexedDB adapter -
    // the premium `getRxStorageIndexedDB` is not used). localStorage's ~5-10MB per-origin quota was exhausted
    // by long games (one permanent `hordeCommits`/`hordeGameEvents` doc per accepted intent, never pruned),
    // which bricked the game with a swallowed `QuotaExceededError`. IndexedDB's quota is dramatically larger
    // (typically a meaningful percentage of free disk space). This is a fresh database name/storage engine:
    // any existing dev game persisted in localStorage under the old adapter is intentionally orphaned - this
    // is an alpha app and migrating that data was judged not worth the complexity.
    //
    // The Angular-signals reactivity plugin is wired so query/document `$$` accessors surface as `Signal`s.
    // The app initializer runs inside the root injection context, so `inject(EnvironmentInjector)` here gives
    // `toSignal` a `DestroyRef` to own the subscriptions.
    const storage = getRxStorageDexie();
    const db = await createRxDatabase<AnimusSignalCollections, unknown, unknown, AngularSignalReactivityLambda>({
        name: HORDE_RXDB_DATABASE_NAME,
        reactivity: createAnimusReactivityFactory(injector),
        storage,
    });

    await db.addCollections<AnimusCollections>({
        customSpawnBrackets: {
            conflictHandler: newestUpdatedAtWinsConflictHandler<CustomSpawnBracketRecord>(),
            schema: customSpawnBracketsSchema,
        },
        customSpawnSimulatorSessions: {
            schema: customSpawnSimulatorSessionsSchema,
        },
        hordeAuthorities: {
            schema: hordeAuthoritiesSchema,
        },
        hordeCommits: {
            // Identity migration: `hordeCommitsSchema` only gained compound indexes (`gameId`+`status`,
            // `gameId`+`updatedAt`), no document shape changed, but RxDB requires a schema version bump - and
            // therefore a migration strategy for that bump - before it will accept a new `indexes` array.
            migrationStrategies: {
                [INDEXED_JOURNAL_SCHEMA_VERSION]: (oldDoc: unknown): unknown => oldDoc,
            },
            schema: hordeCommitsSchema,
        },
        hordeGameEvents: {
            // Identity migration for the same reason as `hordeCommits` above: only the `gameId`+`revision`
            // index was added.
            migrationStrategies: {
                [INDEXED_JOURNAL_SCHEMA_VERSION]: (oldDoc: unknown): unknown => oldDoc,
            },
            schema: hordeGameEventsSchema,
        },
        hordeGameIntents: {
            // Identity migration for the same reason as `hordeCommits` above: only the two compound indexes
            // (`gameId`+`status`, `gameId`+`actorDeviceId`) were added.
            conflictHandler: hordeIntentConflictHandler,
            migrationStrategies: {
                [INDEXED_INTENTS_SCHEMA_VERSION]: (oldDoc: unknown): unknown => oldDoc,
            },
            schema: hordeGameIntentsSchema,
        },
        hordePlayerSecretViews: {
            schema: hordePlayerSecretViewsSchema,
        },
        hordeSessions: {
            schema: hordeSessionsSchema,
        },
    } satisfies AnimusCollectionCreators);

    return db;
}
